import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { environment } from '@environment/environment';
import { ElementService } from '@services/element.service';
import { Auth0TenantGuard } from '@shared/guards/auth0-tenant.guard';
import { KeycloakAuthenticationGuard } from '@shared/security/elements-auth.guard';
import { from, isObservable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AppStateService } from '../layout/app-state.service';
import { getElementInfoByProp } from '../models/element';

export const AuthenticatedElementGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const appState = inject(AppStateService);
  const elementService = inject(ElementService);

  const guardResult = environment.offline
    ? KeycloakAuthenticationGuard(route, state)
    : Auth0TenantGuard(route, state);
  const guard$ = isObservable(guardResult) ? guardResult : from(Promise.resolve(guardResult));

  const hasElementAccess = (userElements: ReturnType<typeof appState.userElements>) => {
    const elementName = (route.paramMap.get('elementName') ?? state.url.split('/')[1])?.toLowerCase();
    const elementInfo = getElementInfoByProp('url', elementName);

    return !elementInfo || !!userElements?.some(element => element.url?.toLowerCase() === `/${elementName}`);
  };

  const denyAccess = () => router.createUrlTree(['/error'], { queryParams: { errorCode: 403 } });

  return guard$.pipe(
    switchMap(result => {
      if (result !== true) {
        return of(result);
      }

      const userElements = appState.userElements();
      if (userElements) {
        return of(hasElementAccess(userElements) ? true : denyAccess());
      }

      const user = appState.user();
      const user$ = user ? of(user) : appState.getUserObservable().pipe(filter(Boolean), take(1));

      return user$.pipe(
        switchMap(userObj => elementService.getElementsForUser(userObj.userId)),
        tap(elements => {
          if (elements?.length) {
            appState.setUserElements(elements);
          }
        }),
        map(elements => (hasElementAccess(elements) ? true : denyAccess())),
        catchError(() => of(true))
      );
    })
  );
};
